/**
 * SGRC Permission Registry — Single Source of Truth
 *
 * Every page, its licensed module, URL path, UI group, and available actions
 * are defined here. All other config files (pagePermissionsConfig,
 * pageGroupsConfig, role-options, createSuperAdmin seed) derive from this.
 *
 * Definitions hub pages use the `definitions*` page key prefix; paths are canonical `/definitions/...`.
 * Coordinate changes with API, web, and MongoDB `roleAuthorization` / `pageKey` fields.
 *
 * `excludeFromPathIndex`: omit from path→key map so another registry row owns the URL
 * (RBAC-only keys or duplicate SPA paths).
 */

// ── Licensed Modules ────────────────────────────────────────────────────────
export const MODULES = {
    CORE: 'core',
    ISO27001: 'iso27001',
    TRAINING: 'training',
    SCF: 'scf',
    KVKK: 'kvkk',
    GDPR: 'gdpr',
    BCM: 'bcm',
    BIGDES: 'bigdes',
    RISK: 'risk',
    ASSET: 'asset',
    AUDIT: 'audit',
    TICKETING: 'ticketing',
    DOCUMENT: 'document',
    INCIDENT: 'incident',
    DIRECTORY: 'directory',
};

// ── Standard Actions ────────────────────────────────────────────────────────
export const BASE_ACTIONS = ['View', 'Add', 'Update', 'Delete'];

// ── UI Group Keys ───────────────────────────────────────────────────────────
export const GROUPS = {
    OTHER: 'other',
    /** Definitions (Tanımlar) — matches main app sidebar hub groups */
    DEFINITIONS_ORGANIZATION: 'definitionsOrganization',
    DEFINITIONS_RISK_CATALOG: 'definitionsRiskCatalog',
    DEFINITIONS_PERSONAL_DATA: 'definitionsPersonalData',
    DEFINITIONS_COMPLIANCE: 'definitionsCompliance',
    DEFINITIONS_DOCUMENT: 'definitionsDocument',
    VENDOR_MANAGEMENT: 'vendorManagement',
    DEFINITIONS_PLATFORM: 'definitionsPlatform',
    ASSET_INVENTORY: 'assetInventory',
    BIGDES: 'bigdes',
    REPORT_OPERATIONS: 'reportOperations',
    ISO27001: 'iso27001',
    BCM: 'bcm',
    GDPR_OPERATIONS: 'gdprOperations',
    RISK_OPERATIONS: 'riskOperations',
    INTERNAL_CONTROL: 'internalControl',
    INTERNAL_AUDIT: 'internalAudit',
    INCIDENT_MANAGEMENT: 'incidentManagement',
    USER_OPERATIONS: 'userOperations',
    TICKETING: 'ticketing',
    PLATFORM_MANAGEMENT: 'platformManagement',
    /** Organizational / ISMS context (4.1) — not ISMS controls */
    ORGANIZATIONAL_CONTEXT: 'organizationalContext',
};

// ── Group Labels (i18n keys) ────────────────────────────────────────────────
export const GROUP_LABELS = {
    [GROUPS.OTHER]: 'permissions.groups.other',
    [GROUPS.DEFINITIONS_ORGANIZATION]: 'permissions.groups.definitionsOrganization',
    [GROUPS.DEFINITIONS_RISK_CATALOG]: 'permissions.groups.definitionsRiskCatalog',
    [GROUPS.DEFINITIONS_PERSONAL_DATA]: 'permissions.groups.definitionsPersonalData',
    [GROUPS.DEFINITIONS_COMPLIANCE]: 'permissions.groups.definitionsCompliance',
    [GROUPS.DEFINITIONS_DOCUMENT]: 'permissions.groups.definitionsDocument',
    [GROUPS.VENDOR_MANAGEMENT]: 'permissions.groups.vendorManagement',
    [GROUPS.DEFINITIONS_PLATFORM]: 'permissions.groups.definitionsPlatform',
    [GROUPS.ASSET_INVENTORY]: 'permissions.groups.assetInventory',
    [GROUPS.BIGDES]: 'permissions.groups.bigdes',
    [GROUPS.REPORT_OPERATIONS]: 'permissions.groups.reportOperations',
    [GROUPS.ISO27001]: 'permissions.groups.iso27001',
    [GROUPS.BCM]: 'permissions.groups.bcm',
    [GROUPS.GDPR_OPERATIONS]: 'permissions.groups.gdprOperations',
    [GROUPS.RISK_OPERATIONS]: 'permissions.groups.riskOperations',
    [GROUPS.INTERNAL_CONTROL]: 'permissions.groups.internalControl',
    [GROUPS.INTERNAL_AUDIT]: 'permissions.groups.internalAudit',
    [GROUPS.INCIDENT_MANAGEMENT]: 'permissions.groups.incidentManagement',
    [GROUPS.USER_OPERATIONS]: 'permissions.groups.userOperations',
    [GROUPS.TICKETING]: 'permissions.groups.ticketing',
    [GROUPS.PLATFORM_MANAGEMENT]: 'permissions.groups.platformManagement',
    [GROUPS.ORGANIZATIONAL_CONTEXT]: 'permissions.groups.organizationalContext',
};

// ── Page Registry ───────────────────────────────────────────────────────────

export const PAGE_REGISTRY = [
    // ─── Core ───────────────────────────────────────────────────────────
    {
        key: 'home',
        module: MODULES.CORE,
        path: '/home',
        additionalPaths: ['/dashboard'],
        group: GROUPS.OTHER,
        actions: ['View'],
    },
    {
        key: 'setup',
        module: MODULES.CORE,
        path: '/setup',
        group: GROUPS.OTHER,
        actions: ['View', 'Update'],
    },
    {
        key: 'myWork',
        module: MODULES.CORE,
        path: '/my-work',
        group: GROUPS.OTHER,
        actions: ['View'],
    },

    // ─── Definitions — Risk / scale ─────────────────────────────────────
    {
        key: 'definitionsScaleRiskAssessmentMatrix',
        module: MODULES.RISK,
        path: '/definitions/scale/risk-assessment-matrix',
        group: GROUPS.DEFINITIONS_RISK_CATALOG,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsScaleBiaImpact',
        module: MODULES.RISK,
        path: '/definitions/scale/bia-impact-scale',
        group: GROUPS.DEFINITIONS_RISK_CATALOG,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'definitionsScaleCiaMatrix',
        module: MODULES.RISK,
        path: '/definitions/scale/cia-matrix',
        group: GROUPS.DEFINITIONS_RISK_CATALOG,
        actions: BASE_ACTIONS,
    },

    // ─── Definitions — KVKK ─────────────────────────────────────────────
    {
        key: 'definitionsKvkkCompany',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/company',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsKvkkCategory',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/category',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsKvkkProcessingPurpose',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/processing-purpose',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsKvkkSubjectPersonGroup',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/subject-person-group',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsKvkkCollectionMethod',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/collection-method',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsKvkkLegalReason',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/legal-reason',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsKvkkTransferType',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/international-transfer/transfer-types',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsKvkkInternationalTransfer',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/international-transfer',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'kvkkDataDisposalMethod',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/data-disposal-method',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'kvkkRecipientGroup',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/recipient-group',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'kvkkDataSource',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/data-source',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'kvkkPrivacyPrinciple',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/privacy-principle',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'kvkkSecurityMeasure',
        module: MODULES.KVKK,
        path: '/definitions/kvkk/security-measure',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },

    // ─── Definitions — Obtaining explicit consent ───────────────────────
    {
        key: 'definitionsObtainingExplicitConsentMethod',
        module: MODULES.KVKK,
        path: '/definitions/obtaining-explicit-consent/method',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsObtainingExplicitConsentReason',
        module: MODULES.KVKK,
        path: '/definitions/obtaining-explicit-consent/reason',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },

    // ─── Definitions — Application tracking ─────────────────────────────
    {
        key: 'definitionsApplicationTrackingApplicationMethod',
        module: MODULES.KVKK,
        path: '/definitions/application-tracking/application-method',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsApplicationTrackingApplicationRequestInformation',
        module: MODULES.KVKK,
        path: '/definitions/application-tracking/application-request-information',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },

    // ─── Definitions — Document ─────────────────────────────────────────
    {
        key: 'definitionsDocumentDocumentType',
        module: MODULES.DOCUMENT,
        path: '/definitions/document/document-type',
        group: GROUPS.DEFINITIONS_DOCUMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsDocumentClassification',
        module: MODULES.DOCUMENT,
        path: '/definitions/document/classification',
        group: GROUPS.DEFINITIONS_DOCUMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsReportDesign',
        module: MODULES.CORE,
        path: '/definitions/report-branding',
        group: GROUPS.REPORT_OPERATIONS,
        actions: BASE_ACTIONS,
    },

    // ─── Vendor management (third-party / TPRM registry) ─────────────────
    {
        key: 'vendorManagementVendors',
        module: MODULES.CORE,
        path: '/vendor-management/vendors',
        group: GROUPS.VENDOR_MANAGEMENT,
        actions: [...BASE_ACTIONS, 'Export'],
    },

    // ─── Definitions — Surveys & platform ─────────────────────────────────
    {
        key: 'definitionsSurveys',
        module: MODULES.CORE,
        path: '/definitions/surveys',
        group: GROUPS.DEFINITIONS_PLATFORM,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsSurveyResponses',
        module: MODULES.CORE,
        path: '/definitions/survey-responses',
        group: GROUPS.DEFINITIONS_PLATFORM,
        actions: ['View'],
    },
    {
        key: 'definitionsAutomations',
        module: MODULES.CORE,
        path: '/definitions/automations',
        group: GROUPS.DEFINITIONS_PLATFORM,
        actions: BASE_ACTIONS,
    },

    // ─── Definitions — Organization ─────────────────────────────────────
    {
        key: 'definitionsGeneralSystemUnit',
        module: MODULES.CORE,
        path: '/definitions/organization/units',
        group: GROUPS.DEFINITIONS_ORGANIZATION,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsGeneralSystemProcess',
        module: MODULES.CORE,
        path: '/definitions/organization/process',
        group: GROUPS.DEFINITIONS_ORGANIZATION,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'definitionsGeneralSystemTeam',
        module: MODULES.CORE,
        path: '/definitions/organization/team',
        group: GROUPS.DEFINITIONS_ORGANIZATION,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsGeneralSystemDutyDefinition',
        module: MODULES.CORE,
        path: '/definitions/organization/duty-definitions',
        group: GROUPS.DEFINITIONS_ORGANIZATION,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsGeneralSystemRootCause',
        module: MODULES.CORE,
        path: '/definitions/organization/root-cause',
        group: GROUPS.DEFINITIONS_ORGANIZATION,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsGeneralSystemMailTemplate',
        module: MODULES.CORE,
        path: '/platform/communications/templates',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },

    // ─── Definitions — Compliance & policy ──────────────────────────────
    {
        key: 'definitionsComplianceRegulatorySources',
        module: MODULES.KVKK,
        path: '/definitions/compliance/regulatory-sources',
        group: GROUPS.DEFINITIONS_COMPLIANCE,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'definitionsComplianceClauses',
        module: MODULES.KVKK,
        path: '/definitions/compliance/clauses',
        group: GROUPS.DEFINITIONS_COMPLIANCE,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsComplianceObligations',
        module: MODULES.KVKK,
        path: '/definitions/compliance/obligations',
        group: GROUPS.DEFINITIONS_COMPLIANCE,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsComplianceFrameworks',
        module: MODULES.KVKK,
        path: '/definitions/compliance/frameworks',
        group: GROUPS.DEFINITIONS_COMPLIANCE,
        actions: BASE_ACTIONS,
    },

    // ─── Definitions — Privacy master (ROPA / DPIA / retention) ─────────
    {
        key: 'kvkkRetentionPeriod',
        module: MODULES.KVKK,
        path: '/definitions/privacy/retention-periods',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'definitionsPrivacyDpiaTemplates',
        module: MODULES.KVKK,
        path: '/definitions/privacy/dpia-templates',
        group: GROUPS.DEFINITIONS_PERSONAL_DATA,
        actions: BASE_ACTIONS,
    },

    {
        key: 'vendorManagementAssessments',
        module: MODULES.ASSET,
        path: '/vendor-management/vendor-assessments',
        group: GROUPS.VENDOR_MANAGEMENT,
        actions: [...BASE_ACTIONS, 'Export'],
    },

    // ─── Definitions — Integrations ─────────────────────────────────────
    {
        key: 'definitionsIntegrationConnectors',
        module: MODULES.CORE,
        path: '/definitions/integrations/connectors',
        group: GROUPS.DEFINITIONS_PLATFORM,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsTenantDirectory',
        module: MODULES.CORE,
        path: '/definitions/integrations/tenant-directory',
        group: GROUPS.DEFINITIONS_PLATFORM,
        actions: BASE_ACTIONS,
    },
    {
        key: 'definitionsIdentityProviders',
        module: MODULES.CORE,
        path: '/definitions/integrations/identity-providers',
        group: GROUPS.DEFINITIONS_PLATFORM,
        actions: BASE_ACTIONS,
    },

    // ─── Definitions — Governance reporting ─────────────────────────────
    {
        key: 'definitionsGovernanceReports',
        module: MODULES.CORE,
        path: '/definitions/compliance/report-definitions',
        group: GROUPS.DEFINITIONS_COMPLIANCE,
        actions: BASE_ACTIONS,
    },

    // ─── Asset Inventory ────────────────────────────────────────────────
    {
        key: 'assetInventoryAssetGroup',
        module: MODULES.BIGDES,
        path: '/asset-inventory-management/asset-group',
        group: GROUPS.ASSET_INVENTORY,
        actions: BASE_ACTIONS,
    },
    {
        key: 'assetInventoryAssetCategory',
        module: MODULES.ASSET,
        path: '/asset-inventory-management/asset-category',
        group: GROUPS.ASSET_INVENTORY,
        actions: BASE_ACTIONS,
    },
    {
        key: 'assetInventoryAssets',
        module: MODULES.ASSET,
        path: '/asset-inventory-management/assets',
        group: GROUPS.ASSET_INVENTORY,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'assetInventoryLicenseManagement',
        module: MODULES.ASSET,
        path: '/asset-inventory-management/license-management',
        group: GROUPS.ASSET_INVENTORY,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'assetInventoryDisposalManagement',
        module: MODULES.ASSET,
        path: '/asset-inventory-management/disposal-management',
        group: GROUPS.ASSET_INVENTORY,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'assetInventoryLifecycleEvents',
        module: MODULES.ASSET,
        path: '/asset-inventory-management/asset-lifecycle-events',
        group: GROUPS.ASSET_INVENTORY,
        actions: BASE_ACTIONS,
    },
    {
        // ROPA / personal-data inventory — privacy modules + GDPR role group (not Asset Inventory bulk).
        key: 'assetInventoryPdInventory',
        module: MODULES.KVKK,
        anyModule: [MODULES.KVKK, MODULES.GDPR],
        path: '/asset-inventory-management/pd-inventory',
        group: GROUPS.GDPR_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },

    {
        key: 'assetInventoryMaintenance',
        module: MODULES.ASSET,
        path: '/asset-inventory-management/maintenance-management',
        group: GROUPS.ASSET_INVENTORY,
        actions: BASE_ACTIONS,
    },
    // ─── BIGDES — Definitions ──────────────────────────────────────────────
    {
        key: 'bigdesDefinitionsCircularArticleDefinitions',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/definitions/circular-article-definitions',
        group: GROUPS.BIGDES,
        actions: BASE_ACTIONS,
    },
    {
        key: 'bigdesDefinitionsMeasures',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/definitions/measures',
        group: GROUPS.BIGDES,
        actions: BASE_ACTIONS,
    },

    // ─── BIGDES — Asset Group Criticality ──────────────────────────────────
    {
        key: 'bigdesAssetGroupEvaluation',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/asset-group-criticality-degree-determination/asset-group-evaluation',
        group: GROUPS.BIGDES,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'bigdesSurveyFilling',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/asset-group-criticality-degree-determination/survey-filling',
        group: GROUPS.BIGDES,
        actions: BASE_ACTIONS,
    },

    // ─── BIGDES — Operations ───────────────────────────────────────────────
    {
        key: 'bigdesMeasureApplicability',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/measure-applicability',
        group: GROUPS.BIGDES,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'bigdesCurrentSituationAnalysis',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/current-situation-analysis',
        group: GROUPS.BIGDES,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'bigdesRoadMap',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/road-map',
        group: GROUPS.BIGDES,
        actions: BASE_ACTIONS,
    },
    {
        key: 'bigdesCompensatoryControl',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/compensatory-control',
        group: GROUPS.BIGDES,
        actions: BASE_ACTIONS,
    },
    // ─── BIGDES — Audit ────────────────────────────────────────────────────
    {
        key: 'bigdesApplicationProcessEvaluation',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/audit/application-process-evaluation',
        group: GROUPS.BIGDES,
        actions: ['View', 'Update', 'Export'],
    },
    {
        key: 'bigdesCurrentSituationEvaluation',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/audit/current-situation-evaluation',
        group: GROUPS.BIGDES,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'bigdesAuditReports',
        module: MODULES.BIGDES,
        path: '/bigdes-operations/audit/audit-reports',
        group: GROUPS.BIGDES,
        actions: ['View', 'Export'],
    },

    // ─── Report Operations ──────────────────────────────────────────────
    {
        key: 'reportForms',
        module: MODULES.DOCUMENT,
        path: '/report-operations/forms',
        group: GROUPS.REPORT_OPERATIONS,
        actions: BASE_ACTIONS,
    },
    {
        key: 'reportDocument',
        module: MODULES.DOCUMENT,
        path: '/report-operations/document',
        group: GROUPS.REPORT_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'reportArchive',
        module: MODULES.CORE,
        path: '/reports/archive',
        group: GROUPS.REPORT_OPERATIONS,
        actions: ['View', 'Add', 'Delete', 'Export'],
    },
    {
        key: 'reportReports',
        module: MODULES.CORE,
        path: '/reports/studio',
        group: GROUPS.REPORT_OPERATIONS,
        actions: ['View', 'Export'],
    },
    {
        key: 'reportMyPendingDocuments',
        module: MODULES.CORE,
        path: '/report-operations/my-pending-documents',
        group: GROUPS.REPORT_OPERATIONS,
        actions: ['View', 'Approve'],
    },

    // ─── ISO 27001 ──────────────────────────────────────────────────────
    {
        key: 'isoContext',
        module: MODULES.ISO27001,
        path: '/context-management/context',
        group: GROUPS.ORGANIZATIONAL_CONTEXT,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'isoCommunication',
        module: MODULES.ISO27001,
        path: '/context-management/communication',
        group: GROUPS.ORGANIZATIONAL_CONTEXT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'isoObjective',
        module: MODULES.ISO27001,
        path: '/isms-controls/objective',
        group: GROUPS.ISO27001,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'isoCompetence',
        module: MODULES.TRAINING,
        path: '/training/competence',
        group: GROUPS.ISO27001,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'isoTraining',
        module: MODULES.TRAINING,
        path: '/training/list',
        group: GROUPS.ISO27001,
        actions: [...BASE_ACTIONS, 'Assign', 'Export'],
    },
    {
        key: 'isoMyTraining',
        module: MODULES.TRAINING,
        path: '/training/my-training',
        group: GROUPS.ISO27001,
        actions: ['View'],
    },
    {
        key: 'isoTrainingVideo',
        module: MODULES.TRAINING,
        path: '/training/video/:trainingId',
        group: GROUPS.ISO27001,
        actions: ['View'],
    },
    {
        key: 'isoMeeting',
        module: MODULES.ISO27001,
        path: '/context-management/meeting',
        group: GROUPS.ORGANIZATIONAL_CONTEXT,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'contextManagementInterestedParties',
        module: MODULES.ISO27001,
        path: '/context-management/interested-parties',
        group: GROUPS.ORGANIZATIONAL_CONTEXT,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'contextManagementScopeDocument',
        module: MODULES.ISO27001,
        path: '/context-management/scope-document',
        group: GROUPS.ORGANIZATIONAL_CONTEXT,
        actions: [...BASE_ACTIONS, 'Export'],
    },

    // ─── BCM ────────────────────────────────────────────────────────────
    {
        key: 'bcmBia',
        module: MODULES.BCM,
        path: '/bcm/bia',
        group: GROUPS.BCM,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'bcmBcp',
        module: MODULES.BCM,
        path: '/bcm/bcp',
        group: GROUPS.BCM,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'bcmCrisisEvent',
        module: MODULES.BCM,
        path: '/bcm/crisis-event',
        group: GROUPS.BCM,
        actions: [...BASE_ACTIONS, 'StartExecution'],
    },
    {
        key: 'bcmExercises',
        module: MODULES.BCM,
        path: '/bcm/exercises',
        group: GROUPS.BCM,
        actions: [...BASE_ACTIONS, 'Export', 'StartExecution'],
    },
    {
        key: 'bcmGlobalSettingsApplications',
        module: MODULES.BCM,
        path: '/bcm/global-settings/applications',
        group: GROUPS.BCM,
        actions: BASE_ACTIONS,
    },
    {
        key: 'bcmGlobalSettingsLocations',
        module: MODULES.BCM,
        path: '/bcm/global-settings/locations',
        group: GROUPS.BCM,
        actions: BASE_ACTIONS,
    },
    {
        key: 'bcmGlobalSettingsVendors',
        module: MODULES.BCM,
        path: '/bcm/global-settings/vendors',
        group: GROUPS.BCM,
        actions: BASE_ACTIONS,
    },
    {
        key: 'bcmGlobalSettingsEmployeesSkills',
        module: MODULES.BCM,
        path: '/bcm/global-settings/employees-skills',
        group: GROUPS.BCM,
        actions: BASE_ACTIONS,
    },

    // ─── GDPR Operations ────────────────────────────────────────────────
    {
        key: 'gdprDashboard',
        module: MODULES.GDPR,
        path: '/gdpr-operations/dashboard',
        group: GROUPS.GDPR_OPERATIONS,
        actions: ['View', 'Export'],
    },
    {
        key: 'gdprObtainingExplicitConsent',
        module: MODULES.GDPR,
        path: '/gdpr-operations/obtaining-explicit-consent',
        group: GROUPS.GDPR_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'gdprApplicationTracking',
        module: MODULES.GDPR,
        path: '/gdpr-operations/application-tracking',
        group: GROUPS.GDPR_OPERATIONS,
        actions: BASE_ACTIONS,
    },
    {
        key: 'gdprDataSubjectRequest',
        module: MODULES.GDPR,
        path: '/gdpr-operations/data-subject-request',
        group: GROUPS.GDPR_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'gdprDataBreach',
        module: MODULES.GDPR,
        path: '/gdpr-operations/data-breach',
        group: GROUPS.GDPR_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'gdprConsentRecord',
        module: MODULES.GDPR,
        path: '/gdpr-operations/consent-record',
        group: GROUPS.GDPR_OPERATIONS,
        actions: BASE_ACTIONS,
    },
    {
        key: 'gdprPrivacyNotice',
        module: MODULES.GDPR,
        path: '/gdpr-operations/privacy-notice',
        group: GROUPS.GDPR_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'gdprDpiaAssessment',
        module: MODULES.GDPR,
        path: '/gdpr-operations/dpia-assessment',
        group: GROUPS.GDPR_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'gdprDisposal',
        module: MODULES.GDPR,
        path: '/gdpr-operations/disposal',
        group: GROUPS.GDPR_OPERATIONS,
        actions: BASE_ACTIONS,
    },
    {
        key: 'gdprVerbis',
        module: MODULES.GDPR,
        path: '/gdpr-operations/verbis',
        group: GROUPS.GDPR_OPERATIONS,
        actions: ['View', 'Update'],
    },
    {
        key: 'gdprDpa',
        module: MODULES.GDPR,
        path: '/gdpr-operations/dpa-registry',
        group: GROUPS.GDPR_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'gdprCookieInventory',
        module: MODULES.GDPR,
        path: '/gdpr-operations/cookie-inventory',
        group: GROUPS.GDPR_OPERATIONS,
        actions: BASE_ACTIONS,
    },

    // ─── Risk Analysis Operations ───────────────────────────────────────
    {
        key: 'riskOperationsMonitoring',
        module: MODULES.RISK,
        path: '/risk-management/risk-monitoring',
        group: GROUPS.RISK_OPERATIONS,
        actions: ['View', 'Export'],
    },
    {
        key: 'riskOperationsRiskRegister',
        module: MODULES.RISK,
        path: '/risk-management/risk-register',
        group: GROUPS.RISK_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'riskOperationsTreatment',
        module: MODULES.RISK,
        path: '/risk-management/risk-treatment',
        group: GROUPS.RISK_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'riskOperationsResidualRiskAcceptance',
        module: MODULES.RISK,
        path: '/risk-management/residual-risk-acceptance',
        group: GROUPS.RISK_OPERATIONS,
        actions: BASE_ACTIONS,
    },
    {
        key: 'riskOperationsRiskOpportunities',
        module: MODULES.RISK,
        path: '/risk-management/risk-opportunities',
        group: GROUPS.RISK_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },

    // ─── Internal Control ───────────────────────────────────────────────
    {
        key: 'internalControlControl',
        module: MODULES.AUDIT,
        path: '/isms-controls/control',
        group: GROUPS.INTERNAL_CONTROL,
        actions: BASE_ACTIONS,
    },
    {
        key: 'internalAuditAssessmentQuestions',
        module: MODULES.AUDIT,
        path: '/isms-controls/assessment-questions',
        group: GROUPS.INTERNAL_CONTROL,
        actions: BASE_ACTIONS,
    },
    {
        key: 'ismsControlsSoa',
        module: MODULES.AUDIT,
        path: '/isms-controls/soa',
        group: GROUPS.INTERNAL_CONTROL,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'ismsControlsThreatIntelligence',
        module: MODULES.RISK,
        path: '/isms-controls/threat-intelligence',
        group: GROUPS.INTERNAL_CONTROL,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'ismsControlsVulnerability',
        module: MODULES.RISK,
        path: '/isms-controls/vulnerability',
        group: GROUPS.INTERNAL_CONTROL,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'internalControlGap',
        module: MODULES.AUDIT,
        path: '/internal-control/gap',
        group: GROUPS.INTERNAL_CONTROL,
        actions: BASE_ACTIONS,
    },
    {
        key: 'internalControlGapAnalysis',
        module: MODULES.AUDIT,
        path: '/internal-control/gap-analysis',
        group: GROUPS.INTERNAL_CONTROL,
        actions: BASE_ACTIONS,
    },
    {
        key: 'internalControlGapResults',
        module: MODULES.AUDIT,
        path: '/internal-control/gap-results',
        group: GROUPS.INTERNAL_CONTROL,
        actions: ['View', 'Export'],
    },

    // ─── Internal Audit ─────────────────────────────────────────────────
    {
        key: 'internalAuditAssessment',
        module: MODULES.AUDIT,
        path: '/audit-compliance/audit-assessment',
        group: GROUPS.INTERNAL_AUDIT,
        actions: [...BASE_ACTIONS, 'Assign', 'Export'],
    },
    {
        key: 'internalAuditAuditCalendar',
        module: MODULES.AUDIT,
        path: '/audit-compliance/audit-program',
        group: GROUPS.INTERNAL_AUDIT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'internalAuditMyAudits',
        module: MODULES.AUDIT,
        path: '/audit-compliance/my-audits',
        group: GROUPS.INTERNAL_AUDIT,
        actions: ['View', 'Update'],
    },
    {
        key: 'auditComplianceCapa',
        module: MODULES.AUDIT,
        path: '/audit-compliance/capa',
        group: GROUPS.INTERNAL_AUDIT,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'auditComplianceKpiDashboard',
        module: MODULES.AUDIT,
        path: '/audit-compliance/kpi-dashboard',
        group: GROUPS.INTERNAL_AUDIT,
        actions: ['View', 'Export'],
    },
    {
        key: 'auditFinding',
        module: MODULES.AUDIT,
        path: '/audit-compliance/audit-findings',
        group: GROUPS.INTERNAL_AUDIT,
        actions: [...BASE_ACTIONS, 'Export'],
    },

    // ─── Incident Management ────────────────────────────────────────────
    {
        key: 'incidentManagementIncidents',
        module: MODULES.INCIDENT,
        path: '/incident-management/incidents',
        group: GROUPS.INCIDENT_MANAGEMENT,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'incidentManagementMonitoring',
        module: MODULES.INCIDENT,
        path: '/incident-management/incidents/monitoring',
        group: GROUPS.INCIDENT_MANAGEMENT,
        actions: ['View'],
    },
    {
        key: 'incidentActions',
        module: MODULES.INCIDENT,
        path: '/incident-management/actions',
        group: GROUPS.INCIDENT_MANAGEMENT,
        actions: [...BASE_ACTIONS, 'Export'],
    },

    // ─── User Operations ────────────────────────────────────────────────
    {
        key: 'userOperationsUserManagement',
        module: MODULES.CORE,
        path: '/user-operations/user-management',
        group: GROUPS.USER_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'userOperationsRoleManagement',
        module: MODULES.CORE,
        path: '/user-operations/role-management',
        group: GROUPS.USER_OPERATIONS,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'userOperationsSystemLogs',
        module: MODULES.CORE,
        path: '/user-operations/system-logs',
        group: GROUPS.USER_OPERATIONS,
        actions: ['View', 'Export'],
    },
    {
        key: 'userOperationsQueueMonitor',
        module: MODULES.CORE,
        path: '/admin/queues',
        group: GROUPS.USER_OPERATIONS,
        actions: ['View', 'Update', 'Delete'],
        excludeFromPathIndex: true,
    },

    // ─── Ticketing & ITSM ───────────────────────────────────────────────
    {
        key: 'itsmServiceCatalog',
        module: MODULES.TICKETING,
        path: '/itsm/service-catalog',
        group: GROUPS.TICKETING,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'itsmChangeRequest',
        module: MODULES.TICKETING,
        path: '/itsm/change-request',
        group: GROUPS.TICKETING,
        actions: [...BASE_ACTIONS, 'Export'],
    },
    {
        key: 'itsmProblem',
        module: MODULES.TICKETING,
        path: '/itsm/problem',
        group: GROUPS.TICKETING,
        actions: BASE_ACTIONS,
    },
    {
        key: 'itsmUnifiedSearch',
        module: MODULES.TICKETING,
        path: '/itsm/hub',
        group: GROUPS.TICKETING,
        actions: ['View'],
    },
    {
        key: 'itsmSupplierContracts',
        module: MODULES.TICKETING,
        path: '/itsm/supplier-contracts',
        group: GROUPS.TICKETING,
        actions: BASE_ACTIONS,
    },
    {
        key: 'ticketing',
        module: MODULES.TICKETING,
        path: '/ticketing',
        group: GROUPS.TICKETING,
        actions: [...BASE_ACTIONS, 'Assign', 'Close', 'Export', 'ViewAll'],
    },
    {
        key: 'ticketComment',
        module: MODULES.TICKETING,
        path: '/ticketing',
        group: GROUPS.TICKETING,
        actions: BASE_ACTIONS,
        excludeFromPathIndex: true,
    },
    {
        key: 'ticketTemplate',
        module: MODULES.TICKETING,
        path: '/ticketing/templates',
        group: GROUPS.TICKETING,
        actions: BASE_ACTIONS,
    },
    {
        key: 'ticketSLA',
        module: MODULES.TICKETING,
        path: '/ticketing/settings',
        group: GROUPS.TICKETING,
        actions: BASE_ACTIONS,
        // ticketConfig owns /ticketing/settings in the path index (127 settings hub).
        excludeFromPathIndex: true,
    },
    {
        key: 'ticketCategory',
        module: MODULES.TICKETING,
        path: '/ticketing/categories',
        group: GROUPS.TICKETING,
        actions: BASE_ACTIONS,
    },
    {
        key: 'ticketConfig',
        module: MODULES.TICKETING,
        path: '/ticketing/settings',
        group: GROUPS.TICKETING,
        actions: ['View', 'Update'],
    },

    // ─── Platform management ───────────────────────────────────────────
    {
        key: 'platformQueueMonitor',
        module: MODULES.CORE,
        path: '/admin/queues',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: ['View', 'Update', 'Delete'],
    },
    {
        key: 'platformPartyTemplates',
        module: MODULES.CORE,
        path: '/platform/party-templates',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'platformAssignmentTemplates',
        module: MODULES.CORE,
        path: '/platform/assignment-templates',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'collectionCampaigns',
        module: MODULES.CORE,
        path: '/platform/collection-campaigns',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'platformWorkflowTemplates',
        module: MODULES.CORE,
        path: '/platform/workflow-templates',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'platformNotificationPolicies',
        module: MODULES.CORE,
        path: '/platform/communications/notification-policies',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: [
            'View',
            'Add',
            'Update',
            'DeleteDraft',
            'Simulate',
            'TestDeliver',
            'LivePilot',
            'ChangeMode',
            'Publish',
            'ViewDispatches',
            'ExportRecipients',
            'RecoverDeliveries',
        ],
    },
    {
        key: 'platformWorkflowTemplates',
        module: MODULES.CORE,
        path: '/platform/workflow-templates/new',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
        excludeFromPathIndex: true,
    },
    {
        key: 'platformWorkflowTemplates',
        module: MODULES.CORE,
        path: '/platform/workflow-templates/:id',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
        excludeFromPathIndex: true,
    },
    /** Runtime instances (API `/workflow-instances`; UI may grow under `/platform/workflow-instances`). */
    {
        key: 'platformWorkflowInstances',
        module: MODULES.CORE,
        path: '/platform/workflow-instances',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    /** Break-glass: force-complete a running workflow instance (separate RBAC from normal instance Update). */
    {
        key: 'workflowEmergencyOverride',
        module: MODULES.CORE,
        path: '/platform/workflow-instances',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: ['Update'],
        excludeFromPathIndex: true,
    },
    {
        key: 'platformBusinessCalendar',
        module: MODULES.CORE,
        path: '/platform/business-calendar',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'platformScheduledReportDistribution',
        module: MODULES.CORE,
        path: '/reports/distributions',
        group: GROUPS.REPORT_OPERATIONS,
        actions: ['View', 'Add', 'Update'],
    },
    /** Workflow tasks list / inbox (API `/workflow-tasks`). */
    {
        key: 'platformWorkflowTasks',
        module: MODULES.CORE,
        path: '/platform/workflow-tasks',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'platformTailorability',
        module: MODULES.CORE,
        path: '/platform/tailorability',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'adminPendingWorkflowOperations',
        module: MODULES.CORE,
        path: '/admin/pending-workflow-operations',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: ['View', 'Update', 'Delete'],
    },
    {
        key: 'adminDelegations',
        module: MODULES.CORE,
        path: '/admin/delegations',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'adminDeadLetterQueue',
        module: MODULES.CORE,
        path: '/admin/dead-letter-queue',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: ['View', 'Update', 'Delete'],
    },
    {
        key: 'adminBackup',
        module: MODULES.CORE,
        path: '/admin/backups',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: ['View', 'Update', 'Delete'],
    },
    {
        key: 'adminWebhooks',
        module: MODULES.CORE,
        path: '/admin/webhooks',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'communicationProvider',
        module: MODULES.CORE,
        path: '/platform/communications/channels',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'platformMfaSettings',
        module: MODULES.CORE,
        path: '/platform/communications/mfa',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: BASE_ACTIONS,
    },
    {
        key: 'platformThirdPartyApplications',
        module: MODULES.CORE,
        path: '/platform/third-party-applications',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: [
            ...BASE_ACTIONS,
            'TestConnection',
            'ManageEndpoints',
            'Invoke',
            'DiscoverSchema',
            'ManageMappings',
            'PreviewImport',
            'Import',
            'ManageSchedules',
            'ViewRuns',
            'ReplayFailures',
            'ManageWebhooks',
            'ExecuteActions',
            'ResolveConflicts',
            'ManageProviders',
            'UpgradeProvider',
        ],
    },
    {
        key: 'adminTenantCompliance',
        module: MODULES.CORE,
        path: '/admin/compliance-frameworks',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: ['View', 'Update'],
    },
    {
        key: 'adminDataExportImport',
        module: MODULES.CORE,
        path: '/admin/data-export-import',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: ['View', 'Export', 'Import', 'Update', 'Commit', 'Retry', 'Reconcile', 'Cancel', 'Compensate'],
    },
    {
        key: 'adminTenantOnboarding',
        module: MODULES.CORE,
        path: '/admin/tenant-onboarding',
        group: GROUPS.PLATFORM_MANAGEMENT,
        actions: ['View', 'Add', 'Update'],
    },
    {
        key: 'userOperationsDelegations',
        module: MODULES.CORE,
        path: '/user-operations/delegations',
        group: GROUPS.OTHER,
        actions: ['View', 'Create', 'Update', 'Delete'],
    },
];
